/**
 * PATH: packages/bills/src/endpoints.ts
 * FILE_ID: BILLS-PKG-ENDPOINTS-001
 * PURPOSE: Single source of truth for `/api/bills/*` URLs.
 *
 * WHY: Phase A shipped `/api/bills/{id}/xero-push/` but the backend
 *      mounts it at `/api/bills/{id}/push-to-xero/`. The 404 only
 *      surfaced on a manual smoke test. Hard-coding URLs in two
 *      hook files in two apps is exactly the bug class this module
 *      eliminates: any change to a path is an edit in one place.
 *
 * BACKEND TRUTH: see `repos/quotech-backend/organisation/urls.py`
 *      lines starting at "api/bills/". Update both files together
 *      if a route ever changes (via expand-migrate-contract:
 *      add the new path here as a fallback first, ship the backend
 *      route, then remove the old path).
 *
 * USAGE:
 *   import { billsEndpoints } from '@quotech/bills';
 *   await client.get(billsEndpoints.list({ status: 'extracted' }));
 *   await client.post(billsEndpoints.pushToXero(billId));
 */

import type {
  BillFlag,
  BillListOrder,
  BillsExportFormat,
  BillSource,
  BillStatus,
  BillStatusFilter,
} from './types';
import { markEmailAsBillPath } from '@quotech/tenant-shared/email/api';

const BASE = '/api/bills';

function withQuery(path: string, params: Record<string, string | undefined>): string {
  const qs = Object.entries(params)
    .filter(([, v]) => v != null && v !== '' && v !== 'all')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v as string)}`)
    .join('&');
  return qs ? `${path}?${qs}` : path;
}

export const billsEndpoints = {
  /**
   * GET — list bills.
   *
   * Filters (all optional):
   *   ``status``    — single BillStatus enum value.
   *   ``source``    — single BillSource enum value ('email' | 'manual').
   *   ``has_xero``  — ``false`` → only bills NOT yet pushed; ``true`` →
   *                   only bills already in Xero. Used by the auto-
   *                   backsync flow with source='manual',
   *                   status='reviewed', has_xero=false to find every
   *                   bill that should be pushed when Xero connects.
   */
  list(filter: {
    status?: BillStatusFilter;
    source?: BillSource;
    has_xero?: boolean;
    /** Server-side search across vendor / invoice / reference / email. */
    q?: string;
    /** Attention-reason filter (queue redesign): unreadable / rejected /
     * no_amount / duplicate. Composes with status + q. */
    flag?: BillFlag;
    /** List ordering. ``triage`` = overdue-first operator queue order;
     * omit / ``received`` for the newest-first default. */
    order?: BillListOrder;
    /** Page size. Omit for the back-compat full page (server caps at 500). */
    limit?: number;
    /** Zero-based offset into the filtered set for infinite scroll. */
    offset?: number;
  } = {}): string {
    return withQuery(`${BASE}/`, {
      status: filter.status,
      source: filter.source,
      has_xero:
        filter.has_xero === undefined ? undefined : String(filter.has_xero),
      q: filter.q,
      flag: filter.flag,
      order: filter.order === 'triage' ? 'triage' : undefined,
      limit: filter.limit === undefined ? undefined : String(filter.limit),
      offset: filter.offset === undefined ? undefined : String(filter.offset),
    });
  },
  /** POST — create a manual bill (no upstream email). The request body
   * is `BillManualCreatePayload`; the response is `BillDetail`. */
  create(): string {
    return `${BASE}/`;
  },
  /** GET — stream a CSV export of the bills register. */
  exportCsv(format: BillsExportFormat, filter: {
    status?: BillStatus;
    source?: BillSource;
  } = {}): string {
    return withQuery(`${BASE}/export/`, {
      format,
      status: filter.status,
      source: filter.source,
    });
  },
  /** GET — bill detail (full payload + line items). */
  detail(billId: string): string {
    return `${BASE}/${billId}/`;
  },
  /** PATCH — update editable fields on a bill. */
  update(billId: string): string {
    return `${BASE}/${billId}/`;
  },
  /** GET — KPI tiles + recent rows for the Billing dashboard. */
  stats(): string {
    return `${BASE}/stats/`;
  },
  /**
   * GET — read-only Xero ↔ system reconciliation diff (no pushes).
   * Live ACCPAY fetch on the backend; both sides filtered to the
   * invoice-date window (defaults to the last 90 days server-side).
   * Backend: BillReconciliationView.
   */
  reconciliation(range: { date_from?: string; date_to?: string } = {}): string {
    return withQuery(`${BASE}/reconciliation/`, {
      date_from: range.date_from,
      date_to: range.date_to,
    });
  },
  /**
   * GET — the authoritative balanced journal preview for a bill (p3c):
   * DR expense per account / DR GST, CR Accounts Payable. Mirrors the
   * pure FE ``buildBillJournal`` (which the panel uses for an instant,
   * offline preview); the endpoint is the server-side source of truth a
   * push-gate can call. Backend: organisation.services.bill_journal.
   */
  journalPreview(billId: string): string {
    return `${BASE}/${billId}/journal-preview/`;
  },
  /**
   * GET — the org's Chart of Accounts for the per-line allocation grid's
   * CoA picker. Lives under the CFO API namespace (report.views.
   * cfo_chart_of_accounts) — the canonical Xero CoA mirror. ``tree=1``
   * also returns the derived class→group→account hierarchy.
   */
  chartOfAccounts(opts: { tree?: boolean; search?: string } = {}): string {
    return withQuery('/api/cfo/chart-of-accounts/', {
      tree: opts.tree ? '1' : undefined,
      search: opts.search,
    });
  },
  /**
   * GET — the org's suppliers (quotebuilder.Supplier) for the Vendor
   * supplier-picker. ``search`` filters server-side. NOTE: this lives under
   * the quotebuilder API namespace (the canonical supplier list — Xero sync,
   * email "Add as Supplier", and manual all write here), not under /api/bills.
   */
  suppliers(search?: string): string {
    return withQuery('/api/quotebuilder/suppliers/', { search });
  },
  /** POST — Run Deepseek extraction over an email's primary attachment. */
  extractFromEmail(emailId: string): string {
    return `${BASE}/extract/${emailId}/`;
  },
  /** POST — Fan out Deepseek extraction across every pending bill. */
  extractPending(): string {
    return `${BASE}/extract-pending/`;
  },
  /** POST — Approve an extracted bill (extracted|failed → reviewed). */
  approve(billId: string): string {
    return `${BASE}/${billId}/approve/`;
  },
  /** POST — Reject a bill with optional reason (→ failed). */
  reject(billId: string): string {
    return `${BASE}/${billId}/reject/`;
  },
  /** POST — Link/unlink a bill to a job (jobId === null clears). */
  linkJob(billId: string): string {
    return `${BASE}/${billId}/link-job/`;
  },
  /**
   * POST — Restore a leak-proof-cascade fail-closed bill (quarantined /
   * awaiting_document / needs_fetch / needs_review) back to its
   * pre_quarantine_status. Operator override + active-learning label.
   * Backend: BillRestoreView.
   */
  restore(billId: string): string {
    return `${BASE}/${billId}/restore/`;
  },
  /** POST — Push the reviewed bill to Xero as a DRAFT ACCPAY invoice. */
  pushToXero(billId: string): string {
    return `${BASE}/${billId}/push-to-xero/`;
  },
  /**
   * POST — Set the bill's linked supplier's internal payment term
   * (CoD/7/30/45/60/90/EOM/Custom) and re-derive this bill's due date.
   * Body: SetSupplierTermInput. Backend: BillSupplierTermView.
   */
  supplierTerm(billId: string): string {
    return `${BASE}/${billId}/supplier-term/`;
  },
  /**
   * POST — Mark the bill as already paid (or clear it). Body: MarkBillPaidInput.
   * Backend: BillMarkPaidView.
   */
  markPaid(billId: string): string {
    return `${BASE}/${billId}/mark-paid/`;
  },
  /**
   * GET — Bank-feed match candidates for the bill (stub until feeds are
   * connected). Backend: BillPaymentSuggestionsView.
   */
  paymentSuggestions(billId: string): string {
    return `${BASE}/${billId}/payment-suggestions/`;
  },
  /** POST — Mark an inbox email as a bill (force-classify + create draft).
   *  Delegates to the shared tenant-shared path so @quotech/email-hub —
   *  which can't import @quotech/bills (circular dep) — and this package
   *  can never drift on the URL. */
  markEmailAsBill(emailId: string): string {
    return markEmailAsBillPath(emailId);
  },
  /**
   * POST — Bulk action on a set of emails. The Bills hub uses this
   * with `action: 'set_category'` to reclassify the email backing a
   * bill (e.g. "this isn't a bill, it's our outgoing invoice"). The
   * backend creates a locked EmailCategoryOverride so the AI won't
   * undo the user's decision on its next pass.
   *
   * Body shape: { email_ids: string[], action: 'set_category', category: string, locked?: boolean }
   * Backend route: POST /api/emails/bulk/  (organisation/views/email_views/stats_actions_views.py:EmailBulkActionView)
   */
  emailBulkAction(): string {
    return `/api/emails/bulk/`;
  },

  /* ──────────────────────────────────────────────────────────────────
   * Purchase orders + bill ↔ PO allocation
   *
   * Backend: organisation/views/po_views/  (added 2026-05-05)
   * Powers the right-rail "PO" tab in the bookkeeping workspace.
   * ────────────────────────────────────────────────────────────────── */

  /** GET — list POs (filter by status / job / supplier / search / source). */
  poList(filter: {
    status?: string;
    job?: number | string;
    supplier?: string;
    search?: string;
    source?: string;
    unallocated?: boolean;
  } = {}): string {
    return withQuery(`/api/purchase-orders/`, {
      status: filter.status,
      job: filter.job != null ? String(filter.job) : undefined,
      supplier: filter.supplier,
      search: filter.search,
      source: filter.source,
      unallocated: filter.unallocated ? 'true' : undefined,
    });
  },
  /** GET / PATCH / DELETE — PO detail. */
  poDetail(poId: number | string): string {
    return `/api/purchase-orders/${poId}/`;
  },
  /** POST — create a manual PO. Same path as poList. */
  poCreate(): string {
    return `/api/purchase-orders/`;
  },
  /** GET — POs scoped to a job (right-rail "available POs" list). */
  poListForJob(jobId: number | string, opts: { include_billed?: boolean } = {}): string {
    return withQuery(`/api/jobs/${jobId}/purchase-orders/`, {
      include_billed: opts.include_billed ? 'true' : undefined,
    });
  },
  /** GET — bill's existing PO allocations + reconciliation summary. */
  billPoAllocations(billId: string): string {
    return `${BASE}/${billId}/po-allocations/`;
  },
  /** POST — create / upsert a bill ↔ PO allocation (idempotent on the pair). */
  billPoAllocationCreate(billId: string): string {
    return `${BASE}/${billId}/po-allocations/`;
  },
  /** DELETE — unallocate a specific PO from a bill. */
  billPoAllocationDetail(billId: string, allocationId: string): string {
    return `${BASE}/${billId}/po-allocations/${allocationId}/`;
  },
  /** GET — ranked auto-match suggestions for the bill (top 10). */
  billPoSuggestions(billId: string): string {
    return `${BASE}/${billId}/po-suggestions/`;
  },

  /* ──────────────────────────────────────────────────────────────────
   * PO payments + convert-to-PO + payment-requests inbox  (PR-2)
   *
   * Backend: organisation/views/po_views/{payment,convert_email,
   * payment_request}_views.py
   * ────────────────────────────────────────────────────────────────── */

  /** GET / POST — list payments on a PO + record a new one. */
  poPayments(poId: number | string): string {
    return `/api/purchase-orders/${poId}/payments/`;
  },
  /** DELETE — remove a specific PoPayment. */
  poPaymentDetail(poId: number | string, payId: string): string {
    return `/api/purchase-orders/${poId}/payments/${payId}/`;
  },
  /** GET — top-10 unmatched XeroPayments that could clear this PO. */
  poPaymentSuggestions(poId: number | string): string {
    return `/api/purchase-orders/${poId}/payment-suggestions/`;
  },
  /** POST — convert an inbound email (quote / payment-request) into a PO. */
  emailConvertToPo(emailId: string): string {
    return `/api/emails/${emailId}/convert-to-po/`;
  },
  /** GET — "POs awaiting payment" inbox lane. */
  paymentRequests(opts: { include_quotes?: boolean } = {}): string {
    return withQuery('/api/payment-requests/', {
      include_quotes: opts.include_quotes ? 'true' : undefined,
    });
  },
} as const;
